import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { CloseIcon } from '../../components/images/svg/icons';
import useOutsideClick from '../../hooks/useOutsideClick';
import '../../scss/components/generic-tooltip.style.scss';


interface GenericTooltipProps {
    isActive: boolean;
    content: React.ReactNode;
    onClose: () => void;
    width?: string;
    position?: string;
    zIndex?: string;
    containerClass?: string;
    tooltipContentClass?: string;
}

const FADE_DURATION = 300;
const DEBOUNCE_DELAY = 150;

const GenericTooltip: React.FC<GenericTooltipProps> = ({
    isActive,
    content,
    onClose,
    width = 'w-75',
    position = 'left-0',
    zIndex = 'z-index-9',
    containerClass = '',
    tooltipContentClass = '',
}) => {
    const ref = useOutsideClick(onClose) as React.MutableRefObject<HTMLDivElement | null>;
    const [visible, setVisible] = useState(isActive);
    const [fadeClass, setFadeClass] = useState('');
    const debounceTimerRef = React.useRef<NodeJS.Timeout | null>(null);
    const fadeTimerRef = React.useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
        if (fadeTimerRef.current) {
            clearTimeout(fadeTimerRef.current);
        }

        debounceTimerRef.current = setTimeout(() => {
            if (isActive) {
                setVisible(true);
                setFadeClass('');
            } else {
                startFadeOut();
            }
        }, DEBOUNCE_DELAY);

        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
            if (fadeTimerRef.current) {
                clearTimeout(fadeTimerRef.current);
            }
        };
    }, [isActive]);

    const startFadeOut = () => {
        setFadeClass('fade-out');
        fadeTimerRef.current = setTimeout(() => {
            setVisible(false);
            onClose();
        }, FADE_DURATION);
    };

    if (!visible) return null;

    return (
        <div ref={ref} className={`position-absolute tooltip-content-bg w-50 w-sm-100  ${fadeClass} ${width} ${zIndex} ${position} rounded-10px ${containerClass}`}>
            <div className={`tooltip-content ${tooltipContentClass}`}>
                <span className="px-2 py-2 top-0 d-block text-wrap">{content}</span>
                <img
                    src={CloseIcon}
                    alt="Close"
                    onClick={onClose}
                    className={`position-absolute close-svg cursor-pointer   ${fadeClass}`}
                    style={{ top: '12px', right: '12px' }}
                />
            </div>
        </div>
    );
}

GenericTooltip.propTypes = {
    isActive: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    width: PropTypes.string,
    position: PropTypes.string,
    zIndex: PropTypes.string,
    containerClass: PropTypes.string,
    tooltipContentClass: PropTypes.string,
};

export default GenericTooltip;